import { useMutation, useQuery } from 'react-query';
import { AxiosError } from 'axios';
import { useToast } from '@common/toast';
import { RemotePHSApplyRepo } from '@repository/phs/apply/PhsApplyRepo';
import PhsApplyRq from '@domain/rq/phs/apply/PhsApplyRq';
import PhsTutorialApplyRq from '@domain/rq/phs/apply/PhsTutorialApplyRq';
import ResultVO from '@domain/vo/phs/test/ResultVO';
import { PhsApplyResultsVO } from '@domain/vo/phs/apply/PhsApplyResultsVO';
import { ProgressStatusList } from '@domain/constant/phsProgressType';

const keys = ['phs', 'apply'];
const PHSApplyStatusKeys = [...keys, 'applyStatus'];

const remotePHSApplyRepo = new RemotePHSApplyRepo();

type ApplyParams = {
  paccYn: boolean;
  pstYn: boolean;
};

const fetchPHSApply = async (params: ApplyParams) => {
  const rq = new PhsApplyRq(params);

  const { data } = await remotePHSApplyRepo.fetchPHSApply(rq);
  return data;
};

const fetchPHSTutorialApply = async (params: ApplyParams) => {
  const rq = new PhsTutorialApplyRq(params);

  const { data } = await remotePHSApplyRepo.fetchPHSTutorialApply(rq);
  return data;
};

function usePHSApply() {
  return useMutation((params: ApplyParams) => fetchPHSApply(params));
}

function usePHSTutorialApply() {
  const Toast = useToast();
  return useMutation((params: ApplyParams) => fetchPHSTutorialApply(params), {
    onError: (error: AxiosError) => {
      console.log(error.response?.data);
      Toast({ type: 'error', iconType: 'info', content: error.response?.data.message });
      return;
    },
  });
}

const fetchPHSApplyHistories = async () => {
  const { data } = await remotePHSApplyRepo.fetchPHSApplyHistories();
  return new PhsApplyResultsVO(data);
};

function usePHSApplyHistoryList(isEnabled: boolean) {
  return useQuery([...keys, 'historyList'], fetchPHSApplyHistories, {
    enabled: isEnabled,
  });
}

const fetchPHSApplyResultList = async () => {
  const { data } = await remotePHSApplyRepo.fetchPHSApplyHistories();
  return {
    paccResults: data.paccResults.map((item) => new ResultVO(item)),
    pstResults: data.pstResults.map((item) => new ResultVO(item)),
  };
};

function usePHSApplyResultList() {
  return useQuery(keys, fetchPHSApplyResultList, {
    initialData: {
      paccResults: [],
      pstResults: [],
    },
  });
}

const fetchPHSApplyStatus = async () => {
  const { data } = await remotePHSApplyRepo.fetchPHSApplyStatus();

  //진행중인지 판단
  const isProgress =
    ProgressStatusList.includes(data.phsApplyStatus.paccApplyStatus?.progressType ?? '') ||
    ProgressStatusList.includes(data.phsApplyStatus.pstApplyStatus?.progressType ?? '');

  return {
    ...data,
    isProgress,
  };
};

function usePHSApplyStatus(isEnabled: boolean) {
  return useQuery(PHSApplyStatusKeys, fetchPHSApplyStatus, {
    enabled: isEnabled,
    refetchOnWindowFocus: true,
  });
}

export {
  keys as phsApplyKeys,
  PHSApplyStatusKeys,
  usePHSApply,
  usePHSTutorialApply,
  usePHSApplyHistoryList,
  usePHSApplyResultList,
  usePHSApplyStatus,
};
